/**
 * Calendar Data Service
 *
 * Aggregates location data into calendar-friendly structures.
 * Groups stops by date and provides date availability information.
 */

import type { Location, Stop } from "@/types/location";

/**
 * A date entry with all locations/stops available on that date
 */
export interface CalendarDate {
  /** Date string in MM/DD/YYYY format */
  date: string;
  /** Parsed Date object for comparison */
  dateObj: Date;
  /** All stops occurring on this date */
  stops: StopWithLocation[];
  /** Aggregated inventory status */
  status: DateStatus;
}

/**
 * Stop with its parent location attached
 */
export interface StopWithLocation extends Stop {
  location: Location;
}

/**
 * Aggregated status for a date
 */
export interface DateStatus {
  /** Total number of locations with stops on this date */
  totalLocations: number;
  /** Number of locations with available inventory */
  availableLocations: number;
  /** Number of locations with limited inventory */
  limitedLocations: number;
  /** Number of sold out locations */
  soldOutLocations: number;
  /** True if ALL locations are sold out */
  allSoldOut: boolean;
  /** True if ANY location has limited inventory */
  hasLimited: boolean;
}

/**
 * Parse date string (MM/DD/YYYY) to Date object
 */
export function parseDate(dateStr: string): Date {
  const [month, day, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format Date object to MM/DD/YYYY string
 */
export function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Aggregate all locations' stops into calendar dates
 *
 * @param locations - All locations with their stops
 * @returns Array of CalendarDate sorted by date ascending
 */
export function aggregateCalendarDates(locations: Location[]): CalendarDate[] {
  const dateMap = new Map<string, StopWithLocation[]>();

  // Collect all stops grouped by date
  for (const location of locations) {
    for (const stop of location.stops) {
      const existing = dateMap.get(stop.date) || [];
      existing.push({ ...stop, location });
      dateMap.set(stop.date, existing);
    }
  }

  // Convert to CalendarDate array
  const calendarDates: CalendarDate[] = [];

  for (const [date, stops] of dateMap) {
    const status = calculateDateStatus(stops);
    calendarDates.push({
      date,
      dateObj: parseDate(date),
      stops,
      status,
    });
  }

  // Sort by date ascending
  calendarDates.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

  return calendarDates;
}

/**
 * Calculate aggregated status for a date's stops
 */
function calculateDateStatus(stops: StopWithLocation[]): DateStatus {
  // Group by location to count unique locations
  const locationMap = new Map<number, StopWithLocation[]>();
  for (const stop of stops) {
    const existing = locationMap.get(stop.location.id) || [];
    existing.push(stop);
    locationMap.set(stop.location.id, existing);
  }

  let availableLocations = 0;
  let limitedLocations = 0;
  let soldOutLocations = 0;

  for (const [, locationStops] of locationMap) {
    // A location is available if ANY of its stops for this date is available
    const hasAvailable = locationStops.some(
      (s) => !s.inventory_status.sold_out
    );
    const hasLimited = locationStops.some(
      (s) => s.inventory_status.hurry_up && !s.inventory_status.sold_out
    );
    const allSoldOut = locationStops.every(
      (s) => s.inventory_status.sold_out
    );

    if (allSoldOut) {
      soldOutLocations++;
    } else if (hasLimited) {
      limitedLocations++;
    } else if (hasAvailable) {
      availableLocations++;
    }
  }

  const totalLocations = locationMap.size;

  return {
    totalLocations,
    availableLocations,
    limitedLocations,
    soldOutLocations,
    allSoldOut: soldOutLocations === totalLocations,
    hasLimited: limitedLocations > 0,
  };
}

/**
 * Filter calendar dates to only include future dates
 */
export function filterFutureDates(dates: CalendarDate[]): CalendarDate[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dates.filter((d) => d.dateObj >= today);
}

/**
 * Get locations available on a specific date
 *
 * @param date - Date string in MM/DD/YYYY format
 * @param locations - All locations
 * @returns Locations that have stops on the given date
 */
export function getLocationsForDate(
  date: string,
  locations: Location[]
): Location[] {
  return locations.filter((location) =>
    location.stops.some((stop) => stop.date === date)
  );
}

/**
 * Get the date range (first and last dates) from calendar dates
 */
export function getDateRange(
  dates: CalendarDate[]
): { start: Date; end: Date } | null {
  if (dates.length === 0) return null;

  return {
    start: dates[0].dateObj,
    end: dates[dates.length - 1].dateObj,
  };
}

/**
 * Month with its days for schedule view
 */
export interface ScheduleMonth {
  /** Month key in YYYY-MM format */
  key: string;
  /** Display label (e.g., "April 2026") */
  label: string;
  /** Days with stops in this month */
  days: ScheduleDay[];
}

/**
 * Day with its stops for schedule view
 */
export interface ScheduleDay {
  /** Date string in MM/DD/YYYY format */
  date: string;
  /** Parsed Date object */
  dateObj: Date;
  /** Display label for day (e.g., "Thursday 03 April") */
  label: string;
  /** Short day label (e.g., "Thu 3") */
  shortLabel: string;
  /** Stops on this day, sorted by time */
  stops: StopWithLocation[];
}

/**
 * Group calendar dates by month for schedule view
 *
 * @param dates - Calendar dates (already filtered to future dates)
 * @returns Array of ScheduleMonth sorted chronologically
 */
export function groupByMonth(dates: CalendarDate[]): ScheduleMonth[] {
  const monthMap = new Map<string, CalendarDate[]>();

  // Group by YYYY-MM
  for (const date of dates) {
    const year = date.dateObj.getFullYear();
    const month = String(date.dateObj.getMonth() + 1).padStart(2, "0");
    const key = `${year}-${month}`;

    const existing = monthMap.get(key) || [];
    existing.push(date);
    monthMap.set(key, existing);
  }

  // Convert to ScheduleMonth array
  const months: ScheduleMonth[] = [];

  for (const [key, calendarDates] of monthMap) {
    const [year, month] = key.split("-").map(Number);
    const monthDate = new Date(year, month - 1, 1);
    const label = monthDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const days: ScheduleDay[] = calendarDates.map((cd) => ({
      date: cd.date,
      dateObj: cd.dateObj,
      label: cd.dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        day: "2-digit",
        month: "long",
      }),
      shortLabel: cd.dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
      }),
      stops: sortStopsByTime(cd.stops),
    }));

    months.push({ key, label, days });
  }

  // Sort months chronologically
  months.sort((a, b) => a.key.localeCompare(b.key));

  return months;
}

/**
 * Sort stops by start time
 */
function sortStopsByTime(stops: StopWithLocation[]): StopWithLocation[] {
  return [...stops].sort((a, b) => {
    // Parse time strings like "10:00 AM" to compare
    const timeA = parseTimeToMinutes(a.start_time);
    const timeB = parseTimeToMinutes(b.start_time);
    return timeA - timeB;
  });
}

/**
 * Parse time string to minutes since midnight for sorting
 */
function parseTimeToMinutes(timeStr: string): number {
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) return 0;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3]?.toUpperCase();

  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  return hours * 60 + minutes;
}
